import React, { useEffect, useState } from 'react';
import {
    Avatar,
    Box,
    Card,
    EmptyState,
    EmptyStateActions,
    EmptyStateDescription,
    EmptyStateIllustration,
    EmptyStateTitle,
    FlexBox,
    Icon,
    Link,
    Spinner,
    Typography,
    tokens,
    useScreenClass,
} from '@vp/swan';
import styled from 'styled-components';
import { Pagination, bugtracker } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { StartNewProjectButton } from '../dashboard/StartNewProjectButton';
import { useListingMyDesignersQuery } from '../queries/ListingMyDesigners.generated';
import { Error } from './Error';
import { PreferredDesignerCTA } from './PreferredDesignerCTA';

const PAGE_SIZE = 20;

type CollaborationState =
    | 'COLLABORATION_STATE_CANCELLED'
    | 'COLLABORATION_STATE_IN_PROGRESS'
    | 'COLLABORATION_STATE_COMPLETED'
    | 'COLLABORATION_STATE_CLOSED';

type CollaborationIconType = 'play' | 'success' | 'cancel';

const TeamCard = styled(Card)`
    border-color: ${tokens.SwanSemColorBorderStandard};
`;

const DesignerSummary = styled(FlexBox)`
    flex: 0 1 240px;
    min-width: 0;
`;

const CollaborationList = styled(FlexBox)`
    flex: 1 1 0;
    min-width: 0;
`;

function getCollaborationDisplay(state: CollaborationState): {
    label: string;
    iconType: CollaborationIconType;
} {
    switch (state) {
        case 'COLLABORATION_STATE_COMPLETED':
        case 'COLLABORATION_STATE_CLOSED':
            return { label: __('Completed project'), iconType: 'success' };
        case 'COLLABORATION_STATE_CANCELLED':
            return { label: __('Cancelled project'), iconType: 'cancel' };
        case 'COLLABORATION_STATE_IN_PROGRESS':
        default:
            return { label: __('Started project'), iconType: 'play' };
    }
}

export const MyDesigners: React.FC = () => {
    const [page, setPage] = useState(1);
    const { data, loading, error } = useListingMyDesignersQuery({
        variables: {
            page,
            pageSize: PAGE_SIZE,
        },
        errorPolicy: 'all',
    });
    const screenClass = useScreenClass();
    const isSmallScreen = screenClass === 'xs' || screenClass === 'sm';
    const designers = data?.myDesigners.designers ?? [];
    const totalPages = data?.myDesigners.totalPages ?? 1;

    // Report partial-failure errors (e.g. individual `user` resolver failures) for
    // observability without blanking the whole page. We only bail to the error
    // screen when there is no data at all.
    useEffect(() => {
        if (error && data) {
            bugtracker.notify(error);
        }
    }, [error, data]);

    if (loading) {
        return (
            <FlexBox alignItems="center" justifyContent="center" mt={10}>
                <Spinner accessibleText={__('Loading')} size="super" />
            </FlexBox>
        );
    }

    if (error && !data) {
        return <Error />;
    }

    if (designers.length === 0 && page === 1) {
        return (
            <Box
                marginY="between-subsections"
                style={{
                    paddingTop: tokens.SwanSemSpaceBetweenSubsections,
                    paddingBottom: tokens.SwanSemSpaceBetweenSubsections,
                }}
            >
                <EmptyState>
                    <EmptyStateIllustration illustrationType="search" />
                    <EmptyStateTitle>
                        {__("You haven't worked with any designers yet.")}
                    </EmptyStateTitle>
                    <EmptyStateDescription>
                        {__(
                            "Once you start a project with a designer, you'll be able to see a list of who you've worked with here."
                        )}
                    </EmptyStateDescription>
                    <EmptyStateActions>
                        <StartNewProjectButton />
                    </EmptyStateActions>
                </EmptyState>
            </Box>
        );
    }

    const handlePageChange = (nextPage: number) => {
        if (nextPage < 1 || nextPage > totalPages || nextPage === page) {
            return;
        }
        setPage(nextPage);
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <FlexBox flexDirection="column" gap="6">
            <Box>
                <Typography fontSkin="title-subsection" mb={4}>
                    {__('My preferred designers')}
                </Typography>
                <Typography fontSkin="body-standard">
                    {__('These designers have worked on one of your design projects before.')}
                </Typography>
            </Box>
            <FlexBox flexDirection="column" gap="3">
                {designers.map((designer) => {
                    const displayName = designer.user?.displayName ?? __('Designer');
                    const avatarUrl = designer.user?.profile.avatarUrl;

                    return (
                        <TeamCard key={designer.userCanonicalId} bordered>
                            <FlexBox
                                flexDirection={isSmallScreen ? 'column' : 'row'}
                                gap={isSmallScreen ? 'between-subsections' : 'between-sections'}
                                alignItems={isSmallScreen ? 'flex-start' : 'center'}
                                justifyContent="flex-start"
                            >
                                <DesignerSummary
                                    alignItems="center"
                                    gap="between-actions"
                                    style={{
                                        width: isSmallScreen ? '100%' : 'auto',
                                        justifyContent: isSmallScreen ? 'flex-start' : 'initial',
                                        flex: isSmallScreen ? '0 1 auto' : undefined,
                                    }}
                                >
                                    {avatarUrl && (
                                        <Box mr="3">
                                            <Avatar src={avatarUrl} alt={displayName} />
                                        </Box>
                                    )}
                                    <Typography fontSkin="body-standard-bold">
                                        {displayName}
                                    </Typography>
                                </DesignerSummary>

                                <CollaborationList flexDirection="column" gap="between-actions">
                                    {designer.collaborations.map((collaboration) => {
                                        const collaborationState =
                                            collaboration.state as CollaborationState;
                                        const display = getCollaborationDisplay(collaborationState);
                                        const collaborationUrl = `/design/collaboration/${collaboration.publicId}`;
                                        const collaborationTitle =
                                            collaboration.title?.trim() || __('Untitled project');

                                        return (
                                            <FlexBox
                                                key={`${collaboration.publicId}-${collaboration.title}`}
                                                alignItems="center"
                                                gap="between-actions"
                                            >
                                                <Icon iconType={display.iconType} skin="subtle" />
                                                <Typography
                                                    fontSkin="body-standard-bold"
                                                    textColor="subtle"
                                                >
                                                    {display.label}
                                                </Typography>
                                                <Link href={collaborationUrl} skin="standard">
                                                    {collaborationTitle}
                                                </Link>
                                            </FlexBox>
                                        );
                                    })}
                                </CollaborationList>
                            </FlexBox>
                        </TeamCard>
                    );
                })}
                {totalPages > 1 && (
                    <FlexBox justifyContent="center">
                        <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            onChange={handlePageChange}
                        />
                    </FlexBox>
                )}
            </FlexBox>
            <PreferredDesignerCTA
                avatarUrls={designers
                    .map((designer) => designer.user?.profile.avatarUrl)
                    .filter((url): url is string => Boolean(url))
                    .slice(0, 3)}
                designerCount={designers.length}
            />
        </FlexBox>
    );
};
